import dayjs, { toUTCDateTimeFormat } from '~/utils/dayjs';

/**
 * Compute a valid date range for analytics/billing queries.
 * Matches backend LineItemCalculator::computeDateTimeRange logic.
 */
export function computeDateTimeRange(periodDate) {
    const periodStart = dayjs.tz(periodDate, 'America/New_York').startOf('day');
    const periodEnd = periodStart.add(1, 'month').startOf('day');
    const now = dayjs.utc().subtract(30, 'minutes').startOf('hour');
    const rangeStart = periodStart.utc();
    const periodEndUtc = periodEnd.utc();
    const rangeEnd = dayjs.min(periodEndUtc, now);

    if (rangeEnd.diff(rangeStart, 'hours', true) < 1) {
        return null;
    }

    return {
        start_date: toUTCDateTimeFormat(rangeStart),
        end_date: toUTCDateTimeFormat(rangeEnd),
    };
}

/**
 * Get current month billing date range for analytics queries.
 * Returns null if less than 1 hour of data is available (first ~90 min of month).
 */
export function getCurrentMonthDateRange() {
    const etNow = dayjs().tz('America/New_York');
    const periodDate = etNow.startOf('month').format('YYYY-MM-DD');
    return computeDateTimeRange(periodDate);
}

/**
 * Get previous month billing date range for analytics queries.
 * Returns null if less than 1 hour of data is available (never for completed past months).
 */
export function getPreviousMonthDateRange() {
    const etNow = dayjs().tz('America/New_York');
    const periodDate = etNow.subtract(1, 'month').startOf('month').format('YYYY-MM-DD');
    return computeDateTimeRange(periodDate);
}

/**
 * Composable for billing-related date and timezone operations
 * Centralizes all billing date logic to ensure consistency across components
 */
export function useBillingDates() {
    /**
     * Format a date string as "MMMM YYYY" (e.g., "January 2024")
     */
    const formatMonthYear = (dateString) => {
        return dayjs(dateString).format('MMMM YYYY');
    };

    /**
     * Get when an invoice will be sent for a given billing period
     * Invoice is sent on the 3rd day of the following month
     * Returns formatted date like "Jan 3"
     */
    const getInvoiceSendDate = (dateString) => {
        const invoiceMonth = dayjs(dateString);

        const sendDate = invoiceMonth.add(1, 'month').startOf('month').add(2, 'days');

        return sendDate.format('MMM D');
    };

    /**
     * Check if we're within the early period of the month (in ET timezone)
     * Used to determine if we should show previous month's invoice
     * Shows previous month until 4pm ET on the 3rd
     */
    const isWithinEarlyMonth = () => {
        const nowET = dayjs().tz('America/New_York');
        const currentDate = nowET.date();
        const currentHour = nowET.hour();

        // Show previous month if:
        // - It's the 1st or 2nd of the month (any time)
        // - It's the 3rd and before 4pm ET (16:00)
        return currentDate < 3 || (currentDate === 3 && currentHour < 16);
    };

    /**
     * Check if we're allowed to edit previous period pricing (before 1PM ET on the 3rd)
     * Used to determine if previous month pricing can be edited in admin
     */
    const canEditPreviousPeriod = () => {
        const nowET = dayjs().tz('America/New_York');
        const currentDate = nowET.date();
        const currentHour = nowET.hour();

        // Allow editing if:
        // - It's the 1st or 2nd of the month (any time)
        // - It's the 3rd and before 1PM ET (13:00)
        return currentDate < 3 || (currentDate === 3 && currentHour < 13);
    };

    /**
     * Get the current month's billing date (start of month)
     */
    const getCurrentBillingMonth = () => {
        const nowET = dayjs().tz('America/New_York');

        return nowET.startOf('month').format('YYYY-MM-DD');
    };

    /**
     * Get the previous month's billing date (start of previous month)
     */
    const getPreviousBillingMonth = () => {
        const nowET = dayjs().tz('America/New_York');

        return nowET.startOf('month').subtract(1, 'month').format('YYYY-MM-DD');
    };

    return {
        formatMonthYear,
        getInvoiceSendDate,
        isWithinEarlyMonth,
        canEditPreviousPeriod,
        getCurrentBillingMonth,
        getPreviousBillingMonth,
    };
}
